import { Icon } from '@evertrue/et-components';
import { Button } from '@evertrue/et-components';
import PropTypes from 'prop-types';
import EverTrue from 'app';

/**
 * Copies text to clipboard and shows a success toast.
 * @param {string} text
 * @param {string} label
 */
const copyToClipboard = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    EverTrue.Alert.success(`${label} copied to clipboard.`);
  } catch {
    EverTrue.Alert.error('Failed to copy. Please copy manually.');
  }
};

/**
 * DNS records table displaying DKIM (TXT) and Return-Path (CNAME) records
 * with per-row copy icons and a "Copy All Records" button.
 */
const DnsRecordsTable = ({ domain }) => {
  const dkimHost = domain.dkim_host || `pm._domainkey.${domain.name}`;
  const dkimValue = domain.dkim_text_value || domain.dkim_value || '';
  const returnPathHost = domain.return_path_host || '';
  const returnPathValue = domain.return_path_value || '';

  const handleCopyAll = () => {
    const allRecords = [
      `Type: TXT`,
      `Host: ${dkimHost}`,
      `Value: ${dkimValue}`,
      ``,
      `Type: CNAME`,
      `Host: ${returnPathHost}`,
      `Value: ${returnPathValue}`,
    ].join('\n');
    copyToClipboard(allRecords, 'All DNS records');
  };

  const rows = [
    { type: 'TXT', subLabel: 'DKIM', host: dkimHost, value: dkimValue, verified: !!domain.dkim_verified },
    {
      type: 'CNAME',
      subLabel: 'Return-Path',
      host: returnPathHost,
      value: returnPathValue,
      verified: !!domain.return_path_verified,
    },
  ];

  return (
    <div className="dns-records-table">
      <table>
        <thead>
          <tr>
            <th>Type</th>
            <th>Host</th>
            <th>Value</th>
            <th aria-label="Copy" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.subLabel}>
              <td>
                <span
                  className={`dns-records-table--type-badge dns-records-table--type-badge--${row.type.toLowerCase()}`}
                >
                  {row.type}
                </span>
                <span className="dns-records-table--sub-label">
                  {row.subLabel}
                  {row.verified && <Icon icon="check-circle" size={1} className="dns-records-table--verified-icon" />}
                </span>
              </td>
              <td className="dns-records-table--mono">{row.host}</td>
              <td className="dns-records-table--mono dns-records-table--value">{row.value}</td>
              <td>
                <button
                  type="button"
                  className="dns-records-table--copy-btn"
                  aria-label={`Copy ${row.subLabel} record`}
                  onClick={() => copyToClipboard(`Host: ${row.host}\nValue: ${row.value}`, `${row.subLabel} record`)}
                >
                  <Icon icon="copy" size={1} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Button type="secondary" className="dns-records-table--copy-all" onClick={handleCopyAll}>
        Copy All Records
      </Button>
    </div>
  );
};

DnsRecordsTable.propTypes = {
  domain: PropTypes.shape({
    name: PropTypes.string.isRequired,
    dkim_host: PropTypes.string,
    dkim_text_value: PropTypes.string,
    dkim_value: PropTypes.string,
    return_path_host: PropTypes.string,
    return_path_value: PropTypes.string,
    dkim_verified: PropTypes.bool,
    return_path_verified: PropTypes.bool,
  }).isRequired,
};

export default DnsRecordsTable;
